import {
  ReactNode,
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  ComponentType,
} from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuthStore } from "../../store/authStore";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import { ThemeToggle } from "../theme/theme-toggle";
import { GlobalScopeFilter } from "../filters/GlobalScopeFilter";
import { SkeletonAvatar } from "../ui/shimmer";
import {
  Menu,
  Users,
  MapPin,
  GraduationCap,
  Lightbulb,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  User,
  Heart,
  MessageSquare,
  Star,
  Building2,
  AlertTriangle,
  ShieldBan,
  FileText,
  BarChart3,
  UserX,
  UserRoundCheck,
  UserRoundX,
  Trash2,
  LayoutDashboard,
  Lock,
  CalendarDays,
  LayoutGrid,
  Dice5,
  SlidersHorizontal,
  Tags,
  KeyRound,
  Megaphone,
  ScrollText,
  Cog,
  Footprints,
  BadgeCheck,
  Sparkles,
  Send,
  BookOpen,
  LucideProps,
} from "lucide-react";

interface DashboardLayoutProps {
  children: ReactNode;
}

type LucideIcon = ComponentType<LucideProps>;

const SIDEBAR_SCROLL_KEY = "hq:sidebar-scroll";
const SIDEBAR_COLLAPSED_KEY = "hq:sidebar-collapsed";
const SIDEBAR_GROUPS_KEY = "hq:sidebar-groups";

interface NavGroup {
  label: string;
  items: Array<{
    path: string;
    label: string;
    icon: LucideIcon;
  }>;
}

// Grouped by *what the page is*, not by who tends to open it. The previous
// split scattered the same kind of page across sections — the three user-state
// pages (locked / banned / deletion) sat in two different groups, and the
// editable catalogues (space categories, interests, map filters, report
// reasons) were spread over four — so finding one meant knowing where it had
// been filed rather than what it was.
//
// Every item also carries a distinct icon on purpose. Icons only help scanning
// if they are unique — previously Heart, Shield, Building2, MapPin, Map,
// Calendar, Activity and MessageSquare each appeared two or three times, so the
// column read as repeated shapes rather than as distinguishable entries.
const NAV_GROUPS: Array<NavGroup & { key: string }> = [
  {
    key: "overview",
    label: "Overview",
    items: [
      { path: "/", label: "Dashboard", icon: LayoutDashboard },
      { path: "/engagement", label: "Engagement", icon: BarChart3 },
    ],
  },
  {
    key: "people",
    label: "People",
    items: [
      { path: "/users", label: "Users", icon: Users },
      { path: "/incomplete-profiles", label: "Incomplete Profiles", icon: UserRoundX },
      { path: "/possible-garbage", label: "Possible Garbage", icon: Trash2 },
      // Was `Unlock`, which reads as the opposite of what the page lists.
      { path: "/locked-users", label: "Locked Users", icon: Lock },
      { path: "/banned-users", label: "Banned Users", icon: ShieldBan },
      { path: "/deletion-requests", label: "Deletion Requests", icon: UserX },
      // Sits directly under deletion: the same queue, read the other way round.
      { path: "/reactivation-requests", label: "Reactivation Requests", icon: UserRoundCheck },
    ],
  },
  {
    key: "content",
    label: "Content",
    items: [
      { path: "/events", label: "Events", icon: CalendarDays },
      { path: "/spaces", label: "Spaces", icon: Building2 },
      { path: "/ideas", label: "Ideas", icon: Lightbulb },
      { path: "/surprise-rolls", label: "Surprise Rolls", icon: Dice5 },
    ],
  },
  {
    key: "places",
    label: "Places",
    items: [
      { path: "/schools", label: "Schools", icon: GraduationCap },
      { path: "/places", label: "Places", icon: MapPin },
      { path: "/popular-places", label: "Popular Places", icon: Star },
    ],
  },
  {
    key: "walky",
    label: "Walky Official",
    items: [
      { path: "/walky-accounts", label: "Walky Accounts", icon: BadgeCheck },
      { path: "/default-content", label: "Default Content", icon: Sparkles },
      { path: "/walky-broadcast", label: "Broadcast DM", icon: Send },
    ],
  },
  {
    key: "moderation",
    label: "Moderation",
    items: [
      { path: "/reports", label: "Content Moderation", icon: AlertTriangle },
      { path: "/messages", label: "Messages", icon: MessageSquare },
    ],
  },
  {
    // The editable reference lists behind the dropdowns and filters elsewhere
    // in the product. `/areas-of-study` had a route and a page but no way in.
    key: "catalogs",
    label: "Catalogs",
    items: [
      { path: "/interests", label: "Interests", icon: Heart },
      { path: "/interest-groups", label: "Interest Groups", icon: Tags },
      { path: "/areas-of-study", label: "Areas of Study", icon: BookOpen },
      {
        path: "/space-categories",
        label: "Space Categories",
        icon: LayoutGrid,
      },
      { path: "/place-types", label: "Map Filters", icon: SlidersHorizontal },
      { path: "/report-reasons", label: "Report Reasons", icon: FileText },
    ],
  },
  {
    key: "system",
    label: "System",
    items: [
      { path: "/roles", label: "Roles & Permissions", icon: KeyRound },
      { path: "/admin/messages", label: "Admin Broadcast", icon: Megaphone },
      { path: "/audit-logs", label: "Audit Logs", icon: ScrollText },
      { path: "/jobs", label: "Background Jobs", icon: Cog },
    ],
  },
  {
    key: "legacy",
    label: "Legacy",
    items: [{ path: "/walks", label: "Walks", icon: Footprints }],
  },
];

// Everything opens except the deprecated section, which stays folded so it
// costs one row rather than two until somebody actually goes looking for it.
const DEFAULT_EXPANDED_GROUPS = NAV_GROUPS.filter(
  (group) => group.key !== "legacy"
).map((group) => group.key);

const readExpandedGroups = (): Set<string> => {
  const saved = localStorage.getItem(SIDEBAR_GROUPS_KEY);
  if (!saved) return new Set(DEFAULT_EXPANDED_GROUPS);
  try {
    const parsed: unknown = JSON.parse(saved);
    if (!Array.isArray(parsed)) return new Set(DEFAULT_EXPANDED_GROUPS);
    return new Set(parsed.filter((key): key is string => typeof key === "string"));
  } catch {
    return new Set(DEFAULT_EXPANDED_GROUPS);
  }
};

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  // Each page renders its own DashboardLayout, so these would otherwise reset
  // on every navigation — collapsing the rail or folding a section only lasted
  // until the next click. They live in localStorage for the same reason the
  // scroll position below does.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1"
  );
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] =
    useState<Set<string>>(readExpandedGroups);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, sidebarCollapsed ? "1" : "0");
  }, [sidebarCollapsed]);

  useEffect(() => {
    localStorage.setItem(
      SIDEBAR_GROUPS_KEY,
      JSON.stringify([...expandedGroups])
    );
  }, [expandedGroups]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase();
  };

  // Every page renders its own DashboardLayout, so navigating unmounts and
  // remounts the sidebar and its scroll position goes back to the top. Keeping
  // it in sessionStorage and restoring before paint makes navigation feel like
  // the sidebar simply stayed put.
  const navRef = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return;

    const saved = sessionStorage.getItem(SIDEBAR_SCROLL_KEY);
    if (saved) nav.scrollTop = Number(saved) || 0;

    const remember = () =>
      sessionStorage.setItem(SIDEBAR_SCROLL_KEY, String(nav.scrollTop));

    nav.addEventListener("scroll", remember, { passive: true });
    return () => nav.removeEventListener("scroll", remember);
  }, []);

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      return newSet;
    });
  };

  const isActive = (path: string) => {
    if (path === "/") {
      return location.pathname === "/";
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Menu */}
      <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
        <SheetTrigger asChild className="lg:hidden fixed top-4 left-4 z-50">
          <Button variant="outline" size="icon">
            <Menu className="h-4 w-4" />
          </Button>
        </SheetTrigger>
        <SheetContent side="left" className="w-80 p-0">
          <div className="flex flex-col h-full">
            {/* Mobile Sidebar Header */}
            <div className="p-6 border-b">
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                  <span className="text-primary-foreground font-bold text-sm">
                    W
                  </span>
                </div>
                <span className="font-bold text-lg">Walky Dashboard</span>
              </Link>
            </div>

            {/* Mobile Navigation */}
            <nav className="flex-1 px-3 py-4 overflow-y-auto">
              <div className="space-y-3">
                {NAV_GROUPS.map((group) => {
                  const isExpanded = expandedGroups.has(group.key);

                  return (
                    <div key={group.key}>
                      {/* Theme tokens, not fixed greys: the previous
                          `hover:bg-gray-100 hover:text-gray-900` flashed a
                          near-white row in the dark theme. */}
                      <button
                        onClick={() => toggleGroup(group.key)}
                        className="flex items-center justify-between w-full px-3 mb-1 h-7 text-[11px] font-semibold text-muted-foreground/70 hover:text-foreground uppercase tracking-[0.08em] transition-colors"
                      >
                        <span>{group.label}</span>
                        {isExpanded ? (
                          <ChevronUp className="h-3 w-3 opacity-60" />
                        ) : (
                          <ChevronDown className="h-3 w-3 opacity-60" />
                        )}
                      </button>

                      {isExpanded && (
                        <ul className="space-y-0.5">
                          {group.items.map((item) => {
                            const ItemIcon = item.icon;
                            const active = isActive(item.path);
                            return (
                              <li key={item.path}>
                                <Link
                                  to={item.path}
                                  aria-current={active ? "page" : undefined}
                                  className={`relative flex items-center gap-3 rounded-md px-3 h-10 text-sm transition-colors ${
                                    active
                                      ? "bg-primary/10 text-primary font-medium"
                                      : "text-muted-foreground hover:bg-accent hover:text-foreground"
                                  }`}
                                  onClick={() => setMobileMenuOpen(false)}
                                >
                                  {active && (
                                    <span className="absolute left-0 top-2 bottom-2 w-0.5 rounded-full bg-primary" />
                                  )}
                                  <ItemIcon className="h-4 w-4 flex-shrink-0" />
                                  <span className="truncate">{item.label}</span>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            </nav>

            {/* Mobile Footer - User Profile & Logout */}
            {user && (
              <div className="p-4 border-t">
                <div className="flex items-center gap-3 mb-3">
                  <Avatar className="h-8 w-8">
                    <AvatarImage
                      src={user.avatar_url}
                      alt={`${user.first_name} ${user.last_name}`}
                    />
                    <AvatarFallback>
                      {getInitials(`${user.first_name} ${user.last_name}`)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col">
                    <p className="text-sm font-medium">{`${user.first_name} ${user.last_name}`}</p>
                    <p className="text-xs text-muted-foreground">
                      {user.email}
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handleLogout();
                  }}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </Button>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex fixed left-0 top-0 h-full bg-gradient-to-b from-card/95 to-card/80 backdrop-blur-sm border-r border-border/50 shadow-lg transition-all duration-300 z-40 ${
          sidebarCollapsed ? "w-16" : "w-64"
        }`}
      >
        <div className="flex flex-col w-full">
          {/* Sidebar Header */}
          <div className="p-4 border-b">
            <div className="flex items-center justify-between">
              {!sidebarCollapsed && (
                <Link to="/" className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                    <span className="text-primary-foreground font-bold text-sm">
                      W
                    </span>
                  </div>
                  <span className="font-bold text-lg">Walky</span>
                </Link>
              )}
              {sidebarCollapsed && (
                <Link
                  to="/"
                  className="flex items-center justify-center w-full"
                >
                  <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                    <span className="text-primary-foreground font-bold text-sm">
                      W
                    </span>
                  </div>
                </Link>
              )}
            </div>
          </div>

          {/* Navigation */}
          <nav
            ref={navRef}
            className="sidebar-scroll flex-1 px-2 py-3 overflow-y-auto"
          >
            <div className="space-y-2">
              {NAV_GROUPS.map((group) => {
                const isExpanded = expandedGroups.has(group.key);

                return (
                  <div key={group.key}>
                    {/* The section header carries no icon: it duplicated one of
                        the item icons below it and added noise to a column that
                        is already icon-dense. */}
                    {!sidebarCollapsed && (
                      <button
                        onClick={() => toggleGroup(group.key)}
                        className="flex items-center justify-between w-full px-2 mb-0.5 h-6 text-[10px] font-semibold text-muted-foreground/70 hover:text-foreground uppercase tracking-[0.08em] transition-colors"
                      >
                        <span>{group.label}</span>
                        {isExpanded ? (
                          <ChevronUp className="h-3 w-3 opacity-60" />
                        ) : (
                          <ChevronDown className="h-3 w-3 opacity-60" />
                        )}
                      </button>
                    )}

                    {/* Collapsed rail: a hairline keeps the grouping readable
                        without a header, instead of a redundant group icon. */}
                    {sidebarCollapsed && (
                      <div className="mx-auto mb-1.5 h-px w-6 bg-border" />
                    )}

                    {(isExpanded || sidebarCollapsed) && (
                      <ul>
                        {group.items.map((item) => {
                          const ItemIcon = item.icon;
                          const active = isActive(item.path);
                          return (
                            <li key={item.path}>
                              <Link
                                to={item.path}
                                aria-current={active ? "page" : undefined}
                                className={`relative flex items-center gap-2.5 rounded-md px-2 h-8 text-[13px] transition-colors ${
                                  sidebarCollapsed ? "justify-center" : ""
                                } ${
                                  active
                                    ? "bg-primary/10 text-primary font-medium"
                                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                                }`}
                                title={
                                  sidebarCollapsed ? item.label : undefined
                                }
                              >
                                {/* Accent rail instead of a solid filled slab —
                                    marks the current page without turning a
                                    whole row into a block of primary colour. */}
                                {active && !sidebarCollapsed && (
                                  <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-primary" />
                                )}
                                <ItemIcon className="h-4 w-4 flex-shrink-0" />
                                {!sidebarCollapsed && (
                                  <span className="truncate">{item.label}</span>
                                )}
                              </Link>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </nav>

          {/* Collapse Toggle */}
          <div className="p-2 border-t">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className={`w-full h-8 text-muted-foreground ${
                sidebarCollapsed ? "justify-center" : "justify-between"
              }`}
            >
              {!sidebarCollapsed && <span className="text-sm">Collapse</span>}
              {sidebarCollapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronLeft className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div
        className={`transition-all duration-300 ${
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-64"
        }`}
      >
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
          <div className="flex h-16 items-center justify-between px-4 lg:px-6">
            <div className="lg:hidden">
              {/* Mobile menu trigger is handled by the Sheet component */}
            </div>

            <div className="flex items-center gap-3 ml-auto">
              <GlobalScopeFilter />
              <ThemeToggle />
              {/* The signed-in user lives up here rather than pinned to the
                  bottom of the sidebar, which is where the nav needs the room. */}
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="h-10 gap-2 px-2">
                      <Avatar className="h-7 w-7">
                        <AvatarImage
                          src={user.avatar_url}
                          alt={`${user.first_name} ${user.last_name}`}
                        />
                        <AvatarFallback>
                          {getInitials(`${user.first_name} ${user.last_name}`)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="hidden md:inline text-sm font-medium">
                        {user.first_name}
                      </span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel>
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium">{`${user.first_name} ${user.last_name}`}</p>
                        <p className="text-xs text-muted-foreground">
                          {user.email}
                        </p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/profile" className="flex items-center">
                        <User className="mr-2 h-4 w-4" />
                        Profile
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleLogout}>
                      <LogOut className="mr-2 h-4 w-4" />
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <SkeletonAvatar className="h-7 w-7" />
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6">{children}</main>
      </div>
    </div>
  );
}
